import type { PayloadAction, CaseReducer } from '@reduxjs/toolkit';
import { current } from '@reduxjs/toolkit';
import { addEditedField } from '../../../../../app/sliceUtils';
import type {
  BenefitsView,
  BenefitsStatusView,
  CallInfoView,
  ComboOfBenefitsIssueView,
  SingleCaseAgreementView,
  VerifyAttributesView,
  AuthorizationView,
} from '../../index';
import type { VerificationSliceState } from '../state';
import { enforceCompletionGate } from './completionGate';

/**
 * Coverage-tab section reducers (spec 005). Each partial-merges its payload into the matching
 * sub-object of `state.current`, marks the section edited, and (where relevant) enforces field
 * invariants — clearing Co-Insurance Max when its toggle goes off, clearing Invalid-* reasons when
 * their toggle goes off, and enforcing the Benefits-Complete gate after status/COB edits.
 */

export const updateBenefits: CaseReducer<VerificationSliceState, PayloadAction<Partial<BenefitsView>>> = (
  state,
  action,
) => {
  if (!state.current) return;
  state.current.benefits = { ...state.current.benefits, ...action.payload };
  // Clearing the toggle resets the conditional Co-Insurance Max value (spec 005 FR-009 / SC-004).
  if (action.payload.hasCoInsuranceMax === false) {
    state.current.benefits.coInsuranceMax = 0;
  }
  addEditedField(state, 'benefits');
};

export const updatePayerCallInfo: CaseReducer<
  VerificationSliceState,
  PayloadAction<Partial<CallInfoView>>
> = (state, action) => {
  if (!state.current) return;
  state.current.callInfo = { ...state.current.callInfo, ...action.payload };
  addEditedField(state, 'callInfo');
};

export const updateBenefitsStatus: CaseReducer<
  VerificationSliceState,
  PayloadAction<Partial<BenefitsStatusView>>
> = (state, action) => {
  if (!state.current) return;
  const next = { ...state.current.benefitsStatus, ...action.payload };
  // Clearing an Invalid-* toggle clears its reason (spec 005 FR-014 / SC-004).
  if (action.payload.invalidPatient === false) next.invalidPatientReason = '';
  if (action.payload.invalidInsurance === false) next.invalidInsuranceReason = '';
  if (action.payload.invalidCodes === false) next.invalidCodeReason = '';
  state.current.benefitsStatus = next;
  enforceCompletionGate(state);
  addEditedField(state, 'benefitsStatus');
};

export const updateComboOfBenefitsIssue: CaseReducer<
  VerificationSliceState,
  PayloadAction<Partial<ComboOfBenefitsIssueView>>
> = (state, action) => {
  if (!state.current) return;
  state.current.comboOfBenefitsIssue = { ...state.current.comboOfBenefitsIssue, ...action.payload };
  enforceCompletionGate(state);
  addEditedField(state, 'comboOfBenefitsIssue');
};

export const updateVerifyAttributes: CaseReducer<
  VerificationSliceState,
  PayloadAction<Partial<VerifyAttributesView>>
> = (state, action) => {
  if (!state.current) return;
  state.current.verifyAttributes = { ...state.current.verifyAttributes, ...action.payload };
  addEditedField(state, 'verifyAttributes');
};

export const updateSingleCaseAgreement: CaseReducer<
  VerificationSliceState,
  PayloadAction<Partial<SingleCaseAgreementView>>
> = (state, action) => {
  if (!state.current) return;
  const base =
    state.current.singleCaseAgreement ?? ({} as SingleCaseAgreementView);
  state.current.singleCaseAgreement = { ...base, ...action.payload };
  addEditedField(state, 'singleCaseAgreement');
};

/**
 * Authorization-tab section reducer (spec 008 FR-018). Partial-merges Authorization fields into
 * `current.authorization` and expresses the cross-field resets IN the reducer (constitution §67/§70,
 * not in components):
 *  - changing `authRequired` resets `authStep` to 'Not Started' (FR-011);
 *  - changing `stayTypeApproved` while `authRequired === 'Required'` resets `authStep` to 'Not Started'
 *    (FR-008 — the case must be re-statused for the new stay type).
 * (The `authStep`-disabled-unless-Required state is a derived UI flag the section reads; not stored.)
 */
export const updateAuthorization: CaseReducer<
  VerificationSliceState,
  PayloadAction<Partial<AuthorizationView>>
> = (state, action) => {
  if (!state.current) return;
  const prev = state.current.authorization;
  const patch = action.payload;
  const next = { ...prev, ...patch };

  const authRequiredChanged =
    patch.authRequired !== undefined && patch.authRequired !== prev.authRequired;
  const stayTypeChanged =
    patch.stayTypeApproved !== undefined && patch.stayTypeApproved !== prev.stayTypeApproved;

  if (authRequiredChanged || (stayTypeChanged && next.authRequired === 'Required')) {
    next.authStep = 'Not Started';
  }

  state.current.authorization = next;
  addEditedField(state, 'authorization');
};

/** Restores `current` from the `server` baseline and clears edit tracking (discard / GO BACK). */
export const discardVerificationEdits: CaseReducer<VerificationSliceState> = (state) => {
  if (state.server) {
    // `state.server` is an Immer draft (a Proxy); unwrap it with `current()` before cloning —
    // native `structuredClone` cannot clone a Proxy (DataCloneError).
    state.current = structuredClone(current(state.server));
  }
  state.isEdited = false;
  state.editedFields = [];
  state.validationErrors = {};
};
